import { useRoomBorrowingStore } from "@/store/room_borrowing/roomBorrowingStore";
import { useState, useEffect } from "react";
import { useRoomBorrowingNext } from "../RoomBorrowing";
import SelectionFilter, {
  Option,
} from "@/components/multi-select-filter/SelectionFilter";
import DateSelect from "../../components/date/DateSelect";
import { useToast } from "@/lib/hooks/useToast";
import { convertShiftsToOptions } from "@/lib/utils";
import { UserFacingError } from "@/models/helper/UserFacingError";
import ShiftService from "@/lib/services/shift/ShiftService";
import { AvailableShiftsResponse } from "@/models/shift/AvailableShiftsResponse";
import { ShiftWithBorrowerCount } from "@/models/shift/ShiftWithBorrowerCount";

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);

const ChooseDates = () => {
  const store = useRoomBorrowingStore();
  const { update: updateStore } = store;
  const { setRoomBorrowingNext } = useRoomBorrowingNext();
  const [shiftsResponse, setShiftsResponse] = useState<AvailableShiftsResponse>(
    { shifts: [], room_capacity: 0 }
  );
  const [selectedShift, setSelectedShift] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleShiftChange = (selected: Option[] | Option | null) => {
    let arr: ShiftWithBorrowerCount[] = [];
    if (Array.isArray(selected)) {
      arr = selected
        .map((element) => {
          const shift = shiftsResponse.shifts.find((f) => f.id == element.id);
          return shift ? shift : null;
        })
        .filter((shift) => shift !== null) as ShiftWithBorrowerCount[];
      setSelectedShift(selected);
    }
    store.update("shift", arr);
  };

  const handleDate = (value: Date | undefined) => {
    if (value !== store.data.date) {
      store.update("date", value);
      setSelectedShift([]);
      store.update("shift", []);
    }
  };

  useEffect(() => {
    const selected = store.data.shift;
    const transformedShifts: Option[] = selected.map((s) => ({
      id: s.id.toString(),
      name: s.name,
    }));
    setSelectedShift(transformedShifts);
    setRoomBorrowingNext(selected !== undefined && selected.length > 0);
  }, [store.data.shift, setRoomBorrowingNext]);

  useEffect(() => {
    if (!store.data.date) return;
    const currentDate = new Date();
    const today = new Date(currentDate.setHours(0, 0, 0, 0));

    const storedDate = new Date(store.data.date);

    const storedDateMidnight = new Date(storedDate.setHours(0, 0, 0, 0));

    if (storedDateMidnight < today) {
      updateStore("date", undefined);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        if (!store.data.date) return;

        const rawShiftsResponse = await ShiftService.getAvailableShifts(
          store.data.date,
          store.data.room.toString(),
          store.data.room_code,
          store.data.room_type.toString(),
          store.data.location.toString()
        );
        setShiftsResponse({
          ...rawShiftsResponse,
          shifts: rawShiftsResponse.shifts.filter(
            (shift) => shift.borrower_count < rawShiftsResponse.room_capacity
          ),
        });
      } catch (error) {
        setShiftsResponse({ shifts: [], room_capacity: 0 });
        if (error instanceof UserFacingError) {
          toast({
            title: error.title,
            description: error.description,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    toast,
    store.data.date,
    store.data.location,
    store.data.room,
    store.data.room_code,
    store.data.room_type,
    updateStore,
  ]);

  return (
    <DateSelect
      title="Select Date"
      date={new Date(store?.data?.date ?? "")}
      loading={loading}
      shiftData={shiftsResponse.shifts}
      onChangeShifts={handleShiftChange}
      onSelectDate={handleDate}
      selectedShifts={selectedShift}
      FilterComponent={
        <SelectionFilter
          multiSelect
          options={convertShiftsToOptions(shiftsResponse.shifts)}
          handleSelected={handleShiftChange}
          selected={selectedShift}
          renderLabel={(o) => (
            <div className="flex flex-col">
              <p>{o.name}</p>
              <p>
                Borrowed:{" "}
                {shiftsResponse.shifts.find((s) => s.id.toString() === o.id)
                  ?.borrower_count || 0}
                /{shiftsResponse.room_capacity}
              </p>
            </div>
          )}
        />
      }
    />
  );
};

export default ChooseDates;
